import React, {
	DragEvent,
	MouseEvent,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react'
import {
	randomString,
	getImageMeta,
	getImageDimensions,
	useImageDimensions,
} from '../../utils'
import {
	UPDATE_QUESTION_ACTIONS,
	UpdateQuestionAction,
} from '../../model/editableQuestion/actions'
import {
	MediaObj,
	QuestionChoice,
	getImageDropdownKey,
} from '@mission.io/question-toolkit'
import { Media, MediaToUpload } from '../../model/editableQuestion/model'
import { InputGroup, UploadedImgContainer } from '../../assets/styles'
import ConfirmationModal from '../modal/Confirmation'
import { MdCloudUpload } from 'react-icons/md'
import { ThumbnailSuccess } from '../../assets/img'
import 'styled-components/macro'
import { styled } from 'styled-components'
import { DropdownImageLabeling } from '../../model/general'
import { processFile } from '../upload-image/fileUploadHelpers'
import { _tempTheme } from '../../utils/tempTheme'
interface DragAndDropProps {
	onChange?: (image: Blob | File) => void
}

//Component that allows drag and drop image.
function DragAndDropImage({ onChange }: DragAndDropProps) {
	const [error, setError] = useState('')
	const processFileCbs = {
		onSuccess: (file: Blob) => {
			setError('')
			onChange && onChange(file)
		},
		onError: setError,
	}

	return (
		<div className="flex justify-center border border-dashed rounded-md p-1 relative flex-1">
			<div
				className="py-4"
				onDragOver={e => e.preventDefault()}
				onDragEnter={e => e.preventDefault()}
				onDragLeave={e => e.preventDefault()}
				onDrop={(e: DragEvent) => {
					e.preventDefault()
					const files = e?.dataTransfer?.files
					if (files && files.length) {
						// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
						processFile(files[0], processFileCbs)
					}
				}}>
				<div className="flex flex-col items-center text-brand-horizon-blue">
					<MdCloudUpload />
					<div>Drag and drop image </div>
				</div>
			</div>

			<input
				type="file"
				aria-label="img-drag-n-drop"
				id={randomString()}
				onChange={e => {
					const files = e.target.files
					if (files && files.length) {
						// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
						processFile(files[0], processFileCbs)
					}
				}}
				accept="image/*"
				className="inset-0 cursor-pointer absolute opacity-0 [&::-webkit-file-upload-button]:cursor-pointer "
			/>

			{error && <div className="text-error">{error}</div>}
		</div>
	)
}

interface ImageCanvasProps {
	questionMedia?: Media[]
	readOnly?: boolean
	onChange?: (update: UpdateQuestionAction) => void
	dropdowns?: Array<{
		x: number
		y: number
		choices: QuestionChoice[]
	}>
	setDropdownIndexSelected: (index: number) => void
	imageProperties: { width: number; height: number }
}

/**
 * A component for Image Labeling that allow uploading an image and creates a Canvas
 * in order to add buttons in specifics points inside the canvas.
 * @param {ImageCanvasProps} props
 * @returns {JSX.Element}
 */
export default function ImageCanvas({
	questionMedia,
	readOnly,
	onChange,
	dropdowns,
	setDropdownIndexSelected,
	imageProperties,
}: ImageCanvasProps): JSX.Element {
	const imageRef = useRef<HTMLImageElement | null>(null)
	const [isOpenModal, setIsOpenModal] = useState(false)

	const questionHasImage = !!questionMedia?.length

	const imageUrl = useMemo(() => {
		if (questionMedia && questionMedia.length) {
			return (
				(questionMedia[0] as MediaToUpload).localUrl ||
				(questionMedia[0] as MediaObj).url ||
				undefined
			)
		}
		return undefined
	}, [questionMedia])

	const dimensions = useImageDimensions(
		questionHasImage ? imageRef : null,
		imageUrl
	)

	const handleChangeQuestionImage = () => {
		if (!readOnly && onChange) {
			setIsOpenModal(false)
			inputRef.current?.click()
		}
	}
	const onChangeInput = (image: Blob) => {
		const { localUrl } = getImageMeta(image)
		if (!readOnly && onChange) {
			getImageDimensions(image)
				.then(({ width, height }: { width: number; height: number }) => {
					onChange({
						type: UPDATE_QUESTION_ACTIONS.CHANGE_QUESTION_IMAGE,
						payload: {
							type: 'IMAGE',
							file: image,
							localUrl: localUrl,
							width,
							height,
						},
					})
				})
				.catch((e: Error) => {
					console.error(e)
					alert('Failed to load image.')
				})
		}
	}
	// Given the dropdown's saved x and y values, translates them to be relative to the canvas.
	const getLocalXY = (x: number, y: number) => {
		if (dimensions) {
			const localX = (x / imageProperties.width) * dimensions.width
			const localY = (y / imageProperties.height) * dimensions.height
			return { x: localX, y: localY }
		}
		return null
	}
	// Given an icons relative x, y values to the canvas, translates them to be relative to the dropdown image
	const getActualXY = (localX: number, localY: number) => {
		if (dimensions) {
			const x = (localX / dimensions.width) * imageProperties.width
			const y = (localY / dimensions.height) * imageProperties.height
			return { x, y }
		}
		return null
	}

	const inputRef = useRef<HTMLInputElement>(null)

	const inputId = useId()
	const uploaderImage = (
		<div css="display: flex;">
			<UploadedImgContainer $disabled={false}>
				{!dropdowns || dropdowns.length === 0 ? (
					<label htmlFor={inputId}>
						<img src={ThumbnailSuccess} />
					</label>
				) : (
					<button
						onClick={() => {
							if (!questionHasImage) return
							if (dropdowns.length > 0) setIsOpenModal(true)
							else handleChangeQuestionImage()
						}}>
						<img src={ThumbnailSuccess} />
					</button>
				)}
				<input
					ref={inputRef}
					css={questionHasImage ? 'display: none;' : ''}
					id={inputId}
					type="file"
					aria-label="Image Uploader"
					onChange={e => {
						e.stopPropagation()
						const files = e.target.files
						if (files && files.length > 0 && onChange) {
							// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
							processFile(files[0], {
								onSuccess: onChangeInput,
								onError: alert,
							})
						}
					}}
					accept=".jpg,.png,.jpeg"
				/>
			</UploadedImgContainer>
		</div>
	)
	return (
		<>
			<InputGroup css="align-items: flex-start;">
				{questionHasImage ? (
					<ImageCanvasWrapper>
						<img
							aria-label="canvas-image"
							src={imageUrl}
							ref={imageRef}
							onMouseUp={(event: MouseEvent) => {
								let coordinates
								if (imageRef.current) {
									const rect = imageRef.current.getBoundingClientRect()
									const localX = event.clientX - rect.left
									const localY = event.clientY - rect.top
									coordinates = getActualXY(localX, localY)
								}
								if (!readOnly && onChange && coordinates) {
									onChange({
										type: UPDATE_QUESTION_ACTIONS.ADD_DROPDOWN,
										payload: coordinates,
									})
									if (dropdowns) setDropdownIndexSelected(dropdowns.length)
								}
							}}
						/>
						{dimensions &&
							dropdowns &&
							dropdowns.map((item: DropdownImageLabeling, index: number) => {
								const coordinates = getLocalXY(item.x, item.y)
								if (!coordinates) return null

								const { x, y } = coordinates
								return (
									<CircleButton
										id={getImageDropdownKey(item)}
										key={`item-${index}`}
										style={{
											left: `${x}px`,
											top: `${y}px`,
										}}
										onClick={() => setDropdownIndexSelected(index)}>
										{index + 1}
									</CircleButton>
								)
							})}
					</ImageCanvasWrapper>
				) : (
					<DragAndDropImage onChange={onChangeInput} />
				)}

				{uploaderImage}
			</InputGroup>
			<ConfirmationModal
				isOpen={isOpenModal}
				confirmText="Delete dropdowns"
				onCancel={() => setIsOpenModal(false)}
				onConfirm={handleChangeQuestionImage}>
				<div>
					<p>
						If you change the image, all of its dropdowns will be deleted. Are
						you sure you want to change the image?
					</p>
				</div>
			</ConfirmationModal>
		</>
	)
}

const ImageCanvasWrapper = styled.div`
	max-width: min(100%, 500px);
	height: auto;
	margin: auto;
	position: relative;
	box-sizing: border-box;
	img {
		max-height: 500px;
		width: 100%;
		height: 100%;
		cursor: pointer;
	}
	button:focus {
		border: 1px solid #ffffff;
	}
`

const CircleButton = styled.button`
	position: absolute;
	color: ${({ theme }) => theme.white};
	padding: ${_tempTheme.spacing1};
	border-radius: 50%;
	width: ${_tempTheme.spacing6};
	height: ${_tempTheme.spacing6};
	background: ${({ theme }) => theme.primary};
	box-sizing: border-box;
	font-style: normal;
	font-family: ${({ theme }) => theme.fontFamily};
	font-weight: ${_tempTheme.font_semibold};
	font-size: ${_tempTheme.text_xs};
	line-height: ${_tempTheme.text_sm};
	transform: translate(-50%, -50%);
	border: none;
	cursor: pointer;
`
